/**
 * Feature Flag Configuration System
 * 
 * This system allows gradual rollout of features without breaking the scaffold.
 * Features can be enabled/disabled based on environment, user type, or manual config.
 */

export interface FeatureConfig {
  // Core Features
  documentUpload: boolean
  documentViewer: boolean
  annotationExtraction: boolean
  annotationEditor: boolean
  extractionPanel: boolean
  boxTool: boolean
  
  // Advanced Features
  cases: boolean
  incomeAnalysis: boolean
  aiAssistant: boolean
  research: boolean
  validation: boolean
  
  // UI Features
  sidebar: boolean
  bottomPanel: boolean
  rightPanel: boolean
  tabs: boolean
  
  // Analysis Features
  transactions: boolean
  calculations: boolean
  reports: boolean
  
  // Collaboration Features
  notes: boolean
  sharing: boolean
  comments: boolean
  
  // Admin Features
  settings: boolean
  userManagement: boolean
  analytics: boolean
}

// Feature Presets for different release phases
export const FEATURE_PRESETS = {
  // Phase 1: Document Extraction Only
  extraction: {
    // Core Features - Enabled
    documentUpload: true,
    documentViewer: true,
    annotationExtraction: true,
    annotationEditor: true,
    extractionPanel: false, // Hide extraction panel for simpler UX
    boxTool: false, // Hide box tool for simpler UX
    
    // Advanced Features - Disabled
    cases: false,
    incomeAnalysis: false,
    aiAssistant: false,
    research: false,
    validation: true, // Keep for extraction verification
    
    // UI Features - Minimal
    sidebar: true, // Enable sidebar to show documents
    bottomPanel: true, // Keep for notes
    rightPanel: true, // Enable for Activity Center
    tabs: true, // Keep for document tabs
    
    // Analysis Features - Disabled
    transactions: false,
    calculations: false,
    reports: false,
    
    // Collaboration Features - Basic
    notes: true, // Keep for annotation notes
    sharing: false,
    comments: false,
    
    // Admin Features - Disabled
    settings: false,
    userManagement: false,
    analytics: false,
  } as FeatureConfig,

  // Phase 2: Basic Analysis
  analysis: {
    documentUpload: true,
    documentViewer: true,
    annotationExtraction: true,
    annotationEditor: true,
    extractionPanel: true,
    boxTool: true,
    cases: true,
    incomeAnalysis: true,
    aiAssistant: true,
    research: false,
    validation: true,
    sidebar: true,
    bottomPanel: true,
    rightPanel: true,
    tabs: true,
    transactions: true,
    calculations: true,
    reports: false,
    notes: true,
    sharing: false,
    comments: false,
    settings: true,
    userManagement: false,
    analytics: false,
  } as FeatureConfig,

  // Phase 3: Full Featured
  full: {
    documentUpload: true,
    documentViewer: true,
    annotationExtraction: true,
    annotationEditor: true,
    extractionPanel: true,
    boxTool: true,
    cases: true,
    incomeAnalysis: true,
    aiAssistant: true,
    research: true,
    validation: true,
    sidebar: true,
    bottomPanel: true,
    rightPanel: true,
    tabs: true,
    transactions: true,
    calculations: true,
    reports: true,
    notes: true,
    sharing: true,
    comments: true,
    settings: true,
    userManagement: true,
    analytics: true,
  } as FeatureConfig,
}

// Get feature config based on environment and settings
export function getFeatureConfig(): FeatureConfig {
  // Check environment variable for preset (Vite uses import.meta.env)
  const preset = import.meta.env.VITE_FEATURE_PRESET || 'extraction'
  
  // Check localStorage for overrides (for development)
  const localOverrides = localStorage.getItem('feature-overrides')
  let overrides: Partial<FeatureConfig> = {}
  
  if (localOverrides) {
    try {
      overrides = JSON.parse(localOverrides)
    } catch (error) {
      console.warn('Invalid feature overrides in localStorage:', error)
    }
  }
  
  // Get base config from preset
  const baseConfig = FEATURE_PRESETS[preset as keyof typeof FEATURE_PRESETS] || FEATURE_PRESETS.extraction
  
  // Apply overrides
  return { ...baseConfig, ...overrides }
}

// Hook for using features in components
export function useFeatureConfig(): FeatureConfig {
  return getFeatureConfig()
}

// Helper function to check if a feature is enabled
export function isFeatureEnabled(feature: keyof FeatureConfig): boolean {
  return getFeatureConfig()[feature]
}

// Development helpers
export function enableFeature(feature: keyof FeatureConfig) {
  const current = getFeatureConfig()
  const overrides = { ...current, [feature]: true }
  localStorage.setItem('feature-overrides', JSON.stringify(overrides))
  window.location.reload()
}

export function disableFeature(feature: keyof FeatureConfig) {
  const current = getFeatureConfig()
  const overrides = { ...current, [feature]: false }
  localStorage.setItem('feature-overrides', JSON.stringify(overrides))
  window.location.reload()
}

export function resetFeatures() {
  localStorage.removeItem('feature-overrides')
  window.location.reload()
}

// Console helpers for development
if (import.meta.env.DEV) {
  (window as any).features = {
    enable: enableFeature,
    disable: disableFeature,
    reset: resetFeatures,
    current: getFeatureConfig,
    presets: FEATURE_PRESETS
  }
  
  console.log('🎛️ Feature flags available in console:', {
    'features.current()': 'Show current config',
    'features.enable("cases")': 'Enable a feature',
    'features.disable("cases")': 'Disable a feature',
    'features.reset()': 'Reset to preset',
    'features.presets': 'Show all presets'
  })
}
